import { useState, useEffect, useCallback } from 'react';
import { lineupService } from '../services/lineupService';
import { UILineupPlayer, MatchFormation } from '../types/database';
import { useOrg } from '../contexts/OrgContext';
import { useTournaments } from './useTournaments';
import { useMatchTimeline } from './useMatchTimeline';

export const useLineups = (matchId: string | undefined, teamAId: string | undefined, teamBId: string | undefined, tournamentId?: string) => {
  const { currentOrganization } = useOrg();
  const { tournaments } = useTournaments();
  
  // Match timeline integration
  const { matchStatus } = useMatchTimeline(matchId || '');

  const [teamASquad, setTeamASquad] = useState<UILineupPlayer[]>([]);
  const [teamBSquad, setTeamBSquad] = useState<UILineupPlayer[]>([]);
  const [teamAFormation, setTeamAFormation] = useState<MatchFormation | null>(null);
  const [teamBFormation, setTeamBFormation] = useState<MatchFormation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchLineupData = useCallback(async () => {
    console.log('useLineups - fetchLineupData called with:', {
      matchId,
      teamAId,
      teamBId,
      organizationId: currentOrganization?.id,
      tournamentsCount: tournaments.length
    });
    
    if (!matchId || !teamAId || !teamBId || !currentOrganization?.id || !tournaments.length) {
      console.log('useLineups - Missing required data, skipping fetch');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the tournament ID from match details if provided, otherwise fall back to first available
      const effectiveTournamentId = tournamentId || tournaments[0]?.id;

      if (!effectiveTournamentId) {
        setError('No tournaments available.');
        setLoading(false);
        return;
      }

      // Fetch squad for Team A
      const squadA = await lineupService.getTeamSquad(teamAId, effectiveTournamentId);
      const lineupA = await lineupService.getMatchLineup(matchId, teamAId);
      const formationA = await lineupService.getMatchFormation(matchId, teamAId);
      
      const uiSquadA: UILineupPlayer[] = squadA.map(player => {
        const inLineup = lineupA?.find(lp => lp.player_id === player.player_id);
        return {
          ...player,
          // Use lineup data if available, otherwise fall back to player's default data
          position: inLineup?.position || player.player?.position || undefined,
          kit_number: inLineup?.kit_number || player.player?.kit_number || undefined,
          identification_description: inLineup?.identification_description || undefined,
          identification_screenshot_url: inLineup?.identification_screenshot_url || undefined,
          isSelected: !!inLineup,
          isCaptain: inLineup?.is_captain || false,
          isStarting: inLineup?.is_starting || false,
          isBench: inLineup?.is_bench || false,
        };
      });
      setTeamASquad(uiSquadA);
      setTeamAFormation(formationA);

      // Fetch squad for Team B
      const squadB = await lineupService.getTeamSquad(teamBId, effectiveTournamentId);
      const lineupB = await lineupService.getMatchLineup(matchId, teamBId);
      const formationB = await lineupService.getMatchFormation(matchId, teamBId);
      
      const uiSquadB: UILineupPlayer[] = squadB.map(player => {
        const inLineup = lineupB?.find(lp => lp.player_id === player.player_id);
        return {
          ...player,
          // Use lineup data if available, otherwise fall back to player's default data
          position: inLineup?.position || player.player?.position || undefined,
          kit_number: inLineup?.kit_number || player.player?.kit_number || undefined,
          identification_description: inLineup?.identification_description || undefined,
          identification_screenshot_url: inLineup?.identification_screenshot_url || undefined,
          isSelected: !!inLineup,
          isCaptain: inLineup?.is_captain || false,
          isStarting: inLineup?.is_starting || false,
          isBench: inLineup?.is_bench || false,
        };
      });
      setTeamBSquad(uiSquadB);
      setTeamBFormation(formationB);

    } catch (err) {
      console.error('Failed to fetch lineup data:', err);
      setError(`Failed to load lineup data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [matchId, teamAId, teamBId, currentOrganization?.id, tournaments, tournamentId]);

  useEffect(() => {
    fetchLineupData();
  }, [fetchLineupData]);
  
  // Refresh lineups when match status changes
  useEffect(() => {
    if (matchStatus) {
      console.log('Match status changed, refreshing lineup data:', matchStatus);
      fetchLineupData();
    }
  }, [matchStatus.status, fetchLineupData]);

  const updatePlayerLineup = useCallback((team: 'A' | 'B', playerId: string, updates: Partial<Pick<UILineupPlayer, 'isSelected' | 'isCaptain' | 'isStarting' | 'isBench' | 'kit_number' | 'position' | 'identification_description' | 'identification_screenshot_url'>>) => {
    if (team === 'A') {
      setTeamASquad(prev => prev.map(player =>
        player.player_id === playerId ? { ...player, ...updates } : player
      ));
    } else {
      setTeamBSquad(prev => prev.map(player =>
        player.player_id === playerId ? { ...player, ...updates } : player
      ));
    }
  }, []);

  const updateFormation = useCallback((team: 'A' | 'B', formation: string, managerName?: string) => {
    if (team === 'A') {
      setTeamAFormation(prev => ({
        id: prev?.id || '',
        match_id: matchId || '',
        team_id: teamAId || '',
        formation,
        manager_name: managerName,
        created_at: prev?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
    } else {
      setTeamBFormation(prev => ({
        id: prev?.id || '',
        match_id: matchId || '',
        team_id: teamBId || '',
        formation,
        manager_name: managerName,
        created_at: prev?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
    }
  }, [matchId, teamAId, teamBId]);

  const saveLineupChanges = useCallback(async () => {
    if (!matchId || !teamAId || !teamBId) {
      setError('Match or team IDs are missing for saving lineup.');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Save lineup changes for both teams
      const lineupUpdatesA = teamASquad.map(player => ({
        playerId: player.player_id,
        isCaptain: player.isCaptain,
        isStarting: player.isStarting,
        isBench: player.isBench,
        isSelected: player.isSelected,
        identificationDescription: player.identification_description,
        identificationScreenshotUrl: player.identification_screenshot_url,
      }));
      await lineupService.updateMatchLineup(matchId, teamAId, lineupUpdatesA);

      const lineupUpdatesB = teamBSquad.map(player => ({
        playerId: player.player_id,
        isCaptain: player.isCaptain,
        isStarting: player.isStarting,
        isBench: player.isBench,
        isSelected: player.isSelected,
        identificationDescription: player.identification_description,
        identificationScreenshotUrl: player.identification_screenshot_url,
      }));
      await lineupService.updateMatchLineup(matchId, teamBId, lineupUpdatesB);

      // Save formation changes for both teams
      if (teamAFormation) {
        await lineupService.updateMatchFormation(matchId, teamAId, teamAFormation.formation, teamAFormation.manager_name);
      }
      if (teamBFormation) {
        await lineupService.updateMatchFormation(matchId, teamBId, teamBFormation.formation, teamBFormation.manager_name);
      }

      // Refetch to ensure UI is in sync with DB after save
      await fetchLineupData();
      return true; // Indicate success
    } catch (err) {
      console.error('Failed to save lineup changes:', err);
      setError('Failed to save lineup changes.');
      return false; // Indicate failure
    } finally {
      setLoading(false);
    }
  }, [matchId, teamAId, teamBId, teamASquad, teamBSquad, teamAFormation, teamBFormation, fetchLineupData]);

  return {
    teamASquad,
    teamBSquad,
    teamAFormation,
    teamBFormation,
    loading,
    error,
    matchStatus,
    updatePlayerLineup,
    updateFormation,
    saveLineupChanges,
    refetchLineupData: fetchLineupData,
  };
};
